import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Star, Camera, Film, Music, Mic, PenTool, Users, MapPin, 
  IndianRupee, Filter, Search, Heart, CheckCircle, Clock, 
  Sparkles, TrendingUp, ArrowRight, Play, X, ChevronDown, 
  Calendar, FileText, Send, Loader2, Shield, Clapperboard, Gamepad2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { ProUpgradePrompt } from "@/components/BrandSubscriptionCard";

const HireTalent = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [talents, setTalents] = useState([]);
  const [filteredTalents, setFilteredTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [shortlist, setShortlist] = useState([]);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [showHireModal, setShowHireModal] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState({ open: false, used: 3, limit: 3 });
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  
  // Booking form state
  const [bookingForm, setBookingForm] = useState({
    project_description: "",
    budget: "",
    date_required: "",
    special_requirements: "",
    booking_type: "project"
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    category: "all",
    location: "",
    budget: [500, 100000],
    language: "all",
    rating: 0
  });

  // Talent categories with colors
  const categories = [
    { id: "models", name: "Models", icon: Camera, color: "#FF4D6D", bgColor: "#FFE4E9" },
    { id: "actors", name: "Actors", icon: Film, color: "#7B2FBE", bgColor: "#F3E8FF" },
    { id: "dancers", name: "Dancers", icon: Music, color: "#FF6B00", bgColor: "#FFF0E6" },
    { id: "voice_artists", name: "Voice Artists", icon: Mic, color: "#1A73E8", bgColor: "#E8F0FE" },
    { id: "photographers", name: "Photographers", icon: Camera, color: "#00A86B", bgColor: "#E6F7F0" },
    { id: "editors", name: "Editors", icon: Clapperboard, color: "#14B8A6", bgColor: "#CCFBF1" },
    { id: "gaming", name: "Gaming", icon: Gamepad2, color: "#8B5CF6", bgColor: "#EDE9FE" },
    { id: "content_writers", name: "Content Writers", icon: PenTool, color: "#FFD700", bgColor: "#FFFBE6" },
  ];

  // Demo talents data
  const demoTalents = [
    {
      id: "t1",
      name: "Priya Sharma",
      category: "models",
      city: "Mumbai",
      image: "https://images.pexels.com/photos/1065084/pexels-photo-1065084.jpeg?w=400",
      hourly_rate: 5000,
      project_rate: 25000,
      rating: 4.9,
      reviews: 28,
      is_verified: true,
      is_featured: true,
      languages: ["Hindi", "English"],
      experience: "5 years",
      bio: "Professional fashion & commercial model with experience in print and digital media."
    },
    {
      id: "t2",
      name: "Rahul Verma",
      category: "actors",
      city: "Delhi",
      image: "https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?w=400",
      hourly_rate: 8000,
      project_rate: 50000,
      rating: 4.8,
      reviews: 15,
      is_verified: true,
      is_featured: false,
      languages: ["Hindi", "English", "Punjabi"],
      experience: "7 years",
      bio: "Theatre trained actor with experience in ads, web series and short films."
    },
    {
      id: "t3",
      name: "Ananya Patel",
      category: "dancers",
      city: "Bangalore",
      image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?w=400",
      hourly_rate: 3000,
      project_rate: 15000,
      rating: 4.7,
      reviews: 42,
      is_verified: true,
      is_featured: true,
      languages: ["Hindi", "English", "Kannada"],
      experience: "8 years",
      bio: "Classical and contemporary dancer, choreographer for brand events."
    },
    {
      id: "t4",
      name: "Vikram Singh",
      category: "voice_artists",
      city: "Mumbai",
      image: "https://images.pexels.com/photos/1681010/pexels-photo-1681010.jpeg?w=400",
      hourly_rate: 2000,
      project_rate: 10000,
      rating: 4.9,
      reviews: 56,
      is_verified: true,
      is_featured: false,
      languages: ["Hindi", "English", "Marathi"],
      experience: "10 years",
      bio: "Professional voice artist for ads, dubbing, audiobooks and IVR."
    },
    {
      id: "t5",
      name: "Sneha Reddy",
      category: "photographers",
      city: "Hyderabad",
      image: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?w=400",
      hourly_rate: 4000,
      project_rate: 20000,
      rating: 4.8,
      reviews: 33,
      is_verified: true,
      is_featured: true,
      languages: ["Telugu", "Hindi", "English"],
      experience: "6 years",
      bio: "Fashion & product photographer with studio setup available."
    },
    {
      id: "t6",
      name: "Arjun Kapoor",
      category: "content_writers",
      city: "Pune",
      image: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?w=400",
      hourly_rate: 1500,
      project_rate: 8000,
      rating: 4.6,
      reviews: 21,
      is_verified: false,
      is_featured: false,
      languages: ["Hindi", "English", "Marathi"],
      experience: "4 years",
      bio: "Creative copywriter for brands, social media content specialist."
    },
    {
      id: "t7",
      name: "Meera Shah",
      category: "models",
      city: "Chennai",
      image: "https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg?w=400",
      hourly_rate: 6000,
      project_rate: 30000,
      rating: 4.9,
      reviews: 19,
      is_verified: true,
      is_featured: false,
      languages: ["Tamil", "English"],
      experience: "3 years",
      bio: "Commercial model specializing in lifestyle and beauty shoots."
    },
    {
      id: "t8",
      name: "Karan Mehta",
      category: "actors",
      city: "Mumbai",
      image: "https://images.pexels.com/photos/1547248/pexels-photo-1547248.jpeg?w=400",
      hourly_rate: 10000,
      project_rate: 75000,
      rating: 5.0,
      reviews: 12,
      is_verified: true,
      is_featured: true,
      languages: ["Hindi", "English", "Gujarati"],
      experience: "12 years",
      bio: "Lead actor in multiple ad films, brand ambassador experience."
    },
  ];

  // Live activity ticker
  const [tickerIndex, setTickerIndex] = useState(0);
  const tickerMessages = [
    { name: "Rahul M.", action: "just got hired by", brand: "Fashion Brand" },
    { name: "Priya S.", action: "received booking from", brand: "Beauty Brand" },
    { name: "Ankit K.", action: "completed project with", brand: "E-commerce Brand" },
    { name: "Sneha R.", action: "got 5-star review from", brand: "Lifestyle Brand" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setTickerIndex((prev) => (prev + 1) % tickerMessages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Simulate loading talents
    setLoading(true);
    setTimeout(() => {
      setTalents(demoTalents);
      setFilteredTalents(demoTalents);
      setLoading(false);
    }, 500);
  }, []);

  // Apply filters
  useEffect(() => {
    let result = [...talents];
    
    if (filters.category !== "all") {
      result = result.filter(t => t.category === filters.category);
    }
    if (filters.location) {
      result = result.filter(t => t.city.toLowerCase().includes(filters.location.toLowerCase()));
    }
    if (filters.budget[0] > 500 || filters.budget[1] < 100000) {
      result = result.filter(t => t.hourly_rate >= filters.budget[0] && t.hourly_rate <= filters.budget[1]);
    }
    if (filters.rating > 0) {
      result = result.filter(t => t.rating >= filters.rating);
    }
    
    setFilteredTalents(result);
  }, [filters, talents]);

  const toggleShortlist = (talentId) => {
    if (shortlist.includes(talentId)) {
      setShortlist(shortlist.filter(id => id !== talentId));
      toast.success("Removed from shortlist");
    } else {
      setShortlist([...shortlist, talentId]);
      toast.success("Added to shortlist!");
    }
  };

  const handleHireClick = (talent) => {
    if (!isAuthenticated) {
      toast.info("Please login to hire talent");
      navigate("/?login=brand");
      return;
    }
    if (user?.user_type !== "brand") {
      toast.error("Only brands can hire talents");
      return;
    }
    setSelectedTalent(talent);
    setBookingForm({
      project_description: "",
      budget: talent.project_rate || "",
      date_required: "",
      special_requirements: "",
      booking_type: "project"
    });
    setShowHireModal(true);
  };

  const handleBookingSubmit = async () => {
    if (!selectedTalent) return;
    
    // Validate form
    if (!bookingForm.project_description.trim()) {
      toast.error("Please describe your project");
      return;
    }
    if (!bookingForm.budget || parseInt(bookingForm.budget) < 500) {
      toast.error("Budget must be at least ₹500");
      return;
    }
    if (!bookingForm.date_required) {
      toast.error("Please select a required date");
      return;
    }
    
    setBookingSubmitting(true);
    try {
      const response = await axios.post(`${API}/talent/booking`, {
        talent_id: selectedTalent.id || selectedTalent.user_id,
        project_description: bookingForm.project_description,
        budget: parseInt(bookingForm.budget),
        date_required: bookingForm.date_required,
        special_requirements: bookingForm.special_requirements,
        booking_type: bookingForm.booking_type
      });
      
      toast.success("🎉 Booking request sent! Talent will respond within 24 hours.");
      setShowHireModal(false);
      setSelectedTalent(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 402 && detail && typeof detail === "object" && detail.code === "BRAND_UPGRADE_REQUIRED") {
        setShowHireModal(false);
        setUpgradePrompt({ open: true, used: detail.used ?? 3, limit: detail.limit ?? 3 });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to send booking request"));
      }
    } finally {
      setBookingSubmitting(false);
    }
  };

  const getCategoryColor = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.color : "#FF4D6D";
  };

  const formatRate = (rate) => {
    if (rate >= 1000) {
      return `₹${(rate / 1000).toFixed(rate % 1000 === 0 ? 0 : 1)}K`;
    }
    return `₹${rate}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navbar */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-lg border-b border-purple-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-10 h-10 bg-gradient-to-br from-[#FF4D6D] to-[#7B2FBE] rounded-xl flex items-center justify-center shadow-lg">
              <Star className="w-6 h-6 text-white" />
            </div>
            <span className="font-outfit font-bold text-xl">
              <span className="text-[#FF4D6D]">FOUND</span>
              <span className="text-[#7B2FBE]">STAR</span>
            </span>
          </div>
          
          <nav className="hidden md:flex items-center gap-6">
            <button onClick={() => navigate("/")} className="text-gray-600 hover:text-[#FF4D6D] transition-colors font-medium">
              Home
            </button>
            <button className="text-[#7B2FBE] font-semibold border-b-2 border-[#7B2FBE] pb-1">
              Hire Talent
            </button>
            <button onClick={() => navigate("/about")} className="text-gray-600 hover:text-[#FF4D6D] transition-colors font-medium">
              About
            </button>
          </nav>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowJoinModal(true)}
              className="border-[#7B2FBE] text-[#7B2FBE] hover:bg-[#7B2FBE] hover:text-white rounded-full hidden sm:flex"
            >
              Join as Talent
            </Button>
            <Button 
              onClick={() => isAuthenticated ? navigate("/brand/hub") : navigate("/?login=brand")}
              className="bg-[#FF4D6D] hover:bg-[#E8405D] text-white rounded-full"
            >
              {isAuthenticated ? "Dashboard" : "Login"}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF4D6D] to-[#7B2FBE]"></div>
        <div className="absolute inset-0 opacity-30" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center">
            {/* Urgency Tag */}
            <div className="inline-flex items-center gap-2 bg-[#FF6B00] text-white px-4 py-2 rounded-full text-sm font-semibold mb-6 animate-pulse">
              <Sparkles className="w-4 h-4" />
              New Talent Marketplace — Join Now!
            </div>
            
            <h1 className="font-outfit font-bold text-4xl md:text-6xl text-white mb-4">
              Find Perfect Talent<br />For Your Brand
            </h1>
            
            <p className="text-white/90 text-lg md:text-xl mb-8 max-w-2xl mx-auto">
              Models • Actors • Dancers • Photographers • Voice Artists
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={() => document.getElementById('talents-section').scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#FF4D6D] hover:bg-[#E8405D] text-white text-lg px-8 py-6 rounded-full shadow-xl hover:shadow-2xl transition-all"
              >
                Browse Talent Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                onClick={() => setShowJoinModal(true)}
                variant="outline"
                className="bg-white text-[#FF4D6D] hover:bg-white/90 border-white text-lg px-8 py-6 rounded-full"
              >
                Join as Talent
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Talent Categories */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="font-outfit font-bold text-3xl text-center text-gray-900 mb-10">
          Browse by Category
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((cat) => (
            <Card 
              key={cat.id}
              onClick={() => setFilters({...filters, category: filters.category === cat.id ? "all" : cat.id})}
              className={`cursor-pointer transition-all duration-300 hover:shadow-xl hover:shadow-pink-200/50 hover:-translate-y-1 border-2 ${
                filters.category === cat.id ? 'border-[#FF4D6D] shadow-lg shadow-pink-200/50' : 'border-transparent'
              }`}
              style={{ backgroundColor: cat.bgColor }}
            >
              <CardContent className="p-6 text-center">
                <div 
                  className="w-14 h-14 mx-auto mb-3 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: cat.color + '20' }}
                >
                  <cat.icon className="w-7 h-7" style={{ color: cat.color }} />
                </div>
                <h3 className="font-semibold text-gray-800">{cat.name}</h3>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Filter Bar */}
      <section className="sticky top-[72px] z-40 bg-[#F3E8FF] border-y border-purple-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                placeholder="Search by city..."
                value={filters.location}
                onChange={(e) => setFilters({...filters, location: e.target.value})}
                className="pl-10 bg-white border-purple-200 focus:border-[#7B2FBE] rounded-full"
              />
            </div>
            
            {/* Category Filter */}
            <select
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              className="px-4 py-2 bg-white border border-purple-200 rounded-full text-sm focus:border-[#7B2FBE] outline-none"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            
            {/* Rating Filter */}
            <select
              value={filters.rating}
              onChange={(e) => setFilters({...filters, rating: parseFloat(e.target.value)})}
              className="px-4 py-2 bg-white border border-purple-200 rounded-full text-sm focus:border-[#7B2FBE] outline-none"
            >
              <option value="0">Any Rating</option>
              <option value="4">4+ Stars</option>
              <option value="4.5">4.5+ Stars</option>
              <option value="4.8">4.8+ Stars</option>
            </select>
            
            {/* Budget Filter Button */}
            <Button 
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`rounded-full ${showFilters ? 'bg-[#FF4D6D] text-white border-[#FF4D6D]' : 'border-purple-200'}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Budget
              <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
            
            {/* Active Filters */}
            {filters.category !== "all" && (
              <Badge 
                className="bg-[#FF4D6D] text-white rounded-full px-3 py-1 cursor-pointer"
                onClick={() => setFilters({...filters, category: "all"})}
              >
                {categories.find(c => c.id === filters.category)?.name} ×
              </Badge>
            )}
          </div>
          
          {/* Budget Slider */}
          {showFilters && (
            <div className="mt-4 p-4 bg-white rounded-xl border border-purple-200">
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
                <IndianRupee className="w-4 h-4 text-[#00A86B]" />
                Hourly Budget: {formatRate(filters.budget[0])} - {formatRate(filters.budget[1])}
              </Label>
              <Slider
                value={filters.budget}
                onValueChange={(value) => setFilters({...filters, budget: value})}
                min={500}
                max={100000}
                step={500}
                className="py-2"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>₹500</span>
                <span>₹1L</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Talents Grid */}
      <section id="talents-section" className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-outfit font-bold text-2xl text-gray-900">
              {filters.category === "all" ? "Featured Talents" : categories.find(c => c.id === filters.category)?.name}
            </h2>
            <p className="text-gray-500">{filteredTalents.length} sample profiles • <span className="text-[#7B2FBE] font-medium">Join to get listed!</span></p>
          </div>
          
          {shortlist.length > 0 && (
            <Badge className="bg-[#1A73E8] text-white">
              <Heart className="w-4 h-4 mr-1 fill-white" />
              {shortlist.length} Shortlisted
            </Badge>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-gray-100 rounded-2xl h-80 animate-pulse"></div>
            ))}
          </div>
        ) : filteredTalents.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-xl text-gray-600 mb-2">No talents found</h3>
            <p className="text-gray-400">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredTalents.map((talent) => (
              <Card 
                key={talent.id}
                className="group overflow-hidden border-2 border-pink-100 hover:border-[#FF4D6D] hover:shadow-xl hover:shadow-pink-200/30 transition-all duration-300"
              >
                <div className="relative">
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={talent.image} 
                      alt={talent.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    
                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex flex-col gap-2">
                      {talent.is_verified && (
                        <Badge className="bg-[#7B2FBE] text-white text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                      {talent.is_featured && (
                        <Badge className="bg-[#FFD700] text-black text-xs">
                          <Sparkles className="w-3 h-3 mr-1" />
                          Featured
                        </Badge>
                      )}
                    </div>
                    
                    {/* Shortlist Button */}
                    <button 
                      onClick={(e) => { e.stopPropagation(); toggleShortlist(talent.id); }}
                      className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                        shortlist.includes(talent.id) 
                          ? 'bg-[#1A73E8] text-white' 
                          : 'bg-white/90 text-gray-600 hover:bg-[#1A73E8] hover:text-white'
                      }`}
                    >
                      <Heart className={`w-4 h-4 ${shortlist.includes(talent.id) ? 'fill-white' : ''}`} />
                    </button>
                    
                    {/* Category Badge */}
                    <div 
                      className="absolute bottom-3 left-3 px-3 py-1 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: getCategoryColor(talent.category) }}
                    >
                      {categories.find(c => c.id === talent.category)?.name}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900">{talent.name}</h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {talent.city}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-full">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="text-sm font-medium">{talent.rating}</span>
                      </div>
                    </div>
                    
                    {/* Rate */}
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-lg font-bold text-[#00A86B]">{formatRate(talent.hourly_rate)}</span>
                      <span className="text-sm text-gray-400">/hour</span>
                      <span className="text-gray-300">|</span>
                      <span className="text-sm text-gray-500">{formatRate(talent.project_rate)}/project</span>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handleHireClick(talent)}
                        className="flex-1 bg-[#FF4D6D] hover:bg-[#E8405D] text-white rounded-full"
                      >
                        Hire Now
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => { setSelectedTalent(talent); }}
                        className="border-[#1A73E8] text-[#1A73E8] hover:bg-[#1A73E8] hover:text-white rounded-full px-3"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Join as Talent CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B00] to-[#FF4D6D]"></div>
        <div className="absolute inset-0" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'%3E%3Ccircle cx='20' cy='20' r='3'/%3E%3C/g%3E%3C/svg%3E\")"}}></div>
        
        <div className="relative max-w-4xl mx-auto px-4 py-16 text-center">
          <h2 className="font-outfit font-bold text-3xl md:text-4xl text-white mb-4">
            Start Earning Today — Join Free!
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">
            Join as a talent and get hired by top brands. Zero commission on your first 3 projects!
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
            <div className="flex items-center gap-2 text-white">
              <CheckCircle className="w-5 h-5 text-[#00FF88]" />
              <span>Free to join</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <CheckCircle className="w-5 h-5 text-[#00FF88]" />
              <span>Secure payments</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <CheckCircle className="w-5 h-5 text-[#00FF88]" />
              <span>Work with top brands</span>
            </div>
          </div>
          
          <Button 
            onClick={() => setShowJoinModal(true)}
            className="bg-white text-[#FF4D6D] hover:bg-gray-100 text-lg px-8 py-6 rounded-full shadow-xl font-semibold"
          >
            Join Now — It's Free
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <p className="mt-6 text-[#00FF88] font-medium">
            🚀 Be Among The First Talents!
          </p>
        </div>
      </section>

      {/* Live Ticker */}
      <div className="bg-gray-900 py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-3 text-white">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#00A86B] rounded-full animate-pulse"></span>
              <span className="text-[#00A86B] font-medium">LIVE</span>
            </span>
            <span className="text-gray-300">
              {tickerMessages[tickerIndex].name} {tickerMessages[tickerIndex].action} <span className="font-semibold text-white">{tickerMessages[tickerIndex].brand}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Hire Modal - Booking Request Form */}
      <Dialog open={showHireModal} onOpenChange={setShowHireModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#FF4D6D] to-[#7B2FBE] rounded-t-lg"></div>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              <Send className="w-5 h-5 text-[#FF4D6D]" />
              Book {selectedTalent?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTalent && (
            <div className="space-y-4">
              {/* Talent Info */}
              <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                <img src={selectedTalent.image || selectedTalent.picture} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md" />
                <div className="flex-1">
                  <h3 className="font-semibold text-[#1E293B]">{selectedTalent.name}</h3>
                  <p className="text-sm text-[#7B2FBE] font-medium">{categories.find(c => c.id === selectedTalent.category)?.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <span className="text-sm font-medium">{selectedTalent.rating}</span>
                    </div>
                    <span className="text-gray-300">|</span>
                    <span className="text-sm text-gray-500">{selectedTalent.city}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-[#00A86B]">{formatRate(selectedTalent.project_rate)}</p>
                  <p className="text-xs text-gray-500">per project</p>
                </div>
              </div>
              
              {/* Booking Form */}
              <div className="space-y-4">
                {/* Project Description */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-[#7B2FBE]" />
                    Project Description *
                  </Label>
                  <Textarea
                    placeholder="Describe your project, requirements, deliverables..."
                    value={bookingForm.project_description}
                    onChange={(e) => setBookingForm({...bookingForm, project_description: e.target.value})}
                    className="min-h-[100px] border-purple-200 focus:border-[#7B2FBE] rounded-xl"
                  />
                </div>
                
                {/* Budget & Date Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                      <IndianRupee className="w-4 h-4 text-[#00A86B]" />
                      Budget (₹) *
                    </Label>
                    <Input
                      type="number"
                      placeholder="e.g., 10000"
                      value={bookingForm.budget}
                      onChange={(e) => setBookingForm({...bookingForm, budget: e.target.value})}
                      className="border-green-200 focus:border-[#00A86B] rounded-xl"
                      min="500"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-[#1A73E8]" />
                      Date Required *
                    </Label>
                    <Input
                      type="date"
                      value={bookingForm.date_required}
                      onChange={(e) => setBookingForm({...bookingForm, date_required: e.target.value})}
                      className="border-blue-200 focus:border-[#1A73E8] rounded-xl"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
                
                {/* Special Requirements */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-[#FFD700]" />
                    Special Requirements (Optional)
                  </Label>
                  <Textarea
                    placeholder="Any specific requirements, location preferences, style notes..."
                    value={bookingForm.special_requirements}
                    onChange={(e) => setBookingForm({...bookingForm, special_requirements: e.target.value})}
                    className="min-h-[60px] border-yellow-200 focus:border-[#FFD700] rounded-xl"
                  />
                </div>
              </div>
              
              {/* Escrow Info */}
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl border border-blue-100">
                <Shield className="w-8 h-8 text-[#1A73E8]" />
                <div>
                  <p className="font-medium text-[#1E293B]">Secure Escrow Payment</p>
                  <p className="text-xs text-gray-500">Your payment is held safely until work is completed & approved</p>
                </div>
              </div>
              
              {/* Response Time Notice */}
              <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-xl text-sm">
                <Clock className="w-4 h-4 text-[#7B2FBE]" />
                <span className="text-[#7B2FBE]">Talent will respond within <strong>24 hours</strong></span>
              </div>
              
              {/* Submit Button */}
              <Button 
                onClick={handleBookingSubmit}
                disabled={bookingSubmitting}
                className="w-full bg-gradient-to-r from-[#FF4D6D] to-[#7B2FBE] hover:opacity-90 text-white py-6 rounded-full text-lg font-semibold shadow-lg"
              >
                {bookingSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending Request...
                  </>
                ) : (
                  <>
                    Send Booking Request
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              
              <p className="text-xs text-center text-gray-400">
                By booking, you agree to our terms. Payment after talent accepts.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Join as Talent Modal */}
      <Dialog open={showJoinModal} onOpenChange={setShowJoinModal}>
        <DialogContent className="sm:max-w-md">
          <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#FF4D6D] to-[#7B2FBE] rounded-t-lg"></div>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Join as Talent</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-600">
              Showcase your talent to thousands of brands. Get hired for photoshoots, events, voiceovers & more!
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle className="w-5 h-5 text-[#00A86B]" />
                <span>Free profile creation</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle className="w-5 h-5 text-[#00A86B]" />
                <span>Get discovered by top brands</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle className="w-5 h-5 text-[#00A86B]" />
                <span>Secure escrow payments</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle className="w-5 h-5 text-[#00A86B]" />
                <span>Zero commission on first 3 projects</span>
              </div>
            </div>
            
            {/* If logged in as influencer, go directly to talent profile */}
            {isAuthenticated && user?.user_type === "influencer" ? (
              <Button 
                onClick={() => {
                  navigate("/influencer/dashboard?tab=talent");
                  setShowJoinModal(false);
                }}
                className="w-full bg-gradient-to-r from-[#FF4D6D] to-[#7B2FBE] hover:opacity-90 text-white py-6 rounded-full text-lg"
              >
                Create Talent Profile
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <>
                <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                  <p className="text-sm text-gray-600 mb-2">Already an influencer on FOUNDSTAR?</p>
                  <p className="text-sm font-medium text-[#7B2FBE]">
                    Login and go to Dashboard → "Talent Profile"
                  </p>
                </div>
                
                <Button 
                  onClick={() => {
                    navigate("/?signup=influencer&talent=true");
                    setShowJoinModal(false);
                  }}
                  className="w-full bg-gradient-to-r from-[#FF4D6D] to-[#7B2FBE] hover:opacity-90 text-white py-6 rounded-full text-lg"
                >
                  Create Free Account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ProUpgradePrompt
        open={upgradePrompt.open}
        used={upgradePrompt.used}
        limit={upgradePrompt.limit}
        onClose={() => setUpgradePrompt({ ...upgradePrompt, open: false })}
        onUpgrade={() => {
          setUpgradePrompt({ ...upgradePrompt, open: false });
          navigate("/brand/profile");
        }}
      />
    </div>
  );
};

export default HireTalent;
